import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FieldValues, useForm } from 'react-hook-form'
import { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { CustomTable } from 'components/customTable'
import { FilterOptions } from 'components/customTable/custom-table-interfaces'
import { SmoothTransition } from 'components/pageTransition'
import { PagePreloader } from 'components/pagePreloader'
import Tooltip from 'components/styledFormElements/Tooltip'
import { PLATFORM_OPTIONS } from 'constants/other'
import { BufferLogRecord, useBufferLogs } from '../../../hooks/buffer-logs'
import { parseSearchParams, getUpdatedSearchParams } from 'services/util'

const PLATFORM_FIELD_ID = 'platform' as const

type TableFieldFilter = {
  id?: string
  value?: unknown
}

const getFieldFilterValue = (fields: TableFieldFilter[] = [], id: string) => {
  const matched = fields.find((field) => field?.id === id)
  return matched?.value
}

const buildBufferLogsRequestParams = (params: Record<string, unknown> = {}) => {
  const { fields = [], ...rest } = params
  const fieldList = Array.isArray(fields) ? (fields as TableFieldFilter[]) : []

  const normalized: Record<string, unknown> = { ...rest }
  delete normalized.search

  const platformValue = getFieldFilterValue(fieldList, PLATFORM_FIELD_ID)
  delete normalized.platform

  if (platformValue !== '' && platformValue !== null && platformValue !== undefined) {
    normalized.deviceType = platformValue
  }

  return normalized
}

const formatDateTime = (value: string | null | undefined) => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('DD MMM YYYY, hh:mm:ss A') : '-'
}

/** `start_time` / `end_time` from API are Unix timestamps in milliseconds. */
const formatEpochMsToDateTime = (epochMs: number | null | undefined): string => {
  if (epochMs === null || epochMs === undefined || Number.isNaN(Number(epochMs))) {
    return '-'
  }
  const m = moment(Number(epochMs))
  return m.isValid() ? m.format('DD MMM YYYY, hh:mm:ss A') : '-'
}

const formatBufferIntervalLines = (intervals: NonNullable<BufferLogRecord['buffer']>) =>
  intervals.map(
    (b, i) =>
      `${i + 1}. ${formatEpochMsToDateTime(b.start_time)} → ${formatEpochMsToDateTime(b.end_time)}`
  )

const BufferIntervalsCell = ({
  intervals,
}: {
  intervals: BufferLogRecord['buffer'] | undefined
}) => {
  if (!intervals?.length) {
    return <span className="text-gray-500 dark:text-gray-400">-</span>
  }

  const lines = formatBufferIntervalLines(intervals)
  const count = intervals.length

  return (
    <Tooltip
      title="Buffer intervals"
      position="bottom"
      widthClass="w-[min(28rem,90vw)] max-h-60 overflow-y-auto text-left text-[11px] leading-snug"
      items={lines}
    >
      <span className="inline-flex cursor-help items-center gap-1 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-xs text-gray-700 underline decoration-dotted underline-offset-2 dark:border-slate-600 dark:bg-slate-800/80 dark:text-gray-200">
        <span className="font-medium text-blue-600 dark:text-blue-400">{count}</span>
        <span>interval{count !== 1 ? 's' : ''}</span>
      </span>
    </Tooltip>
  )
}

const BufferLogs: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const filterParams: Record<string, any> = parseSearchParams(searchParams, 'filter')
  const [rowSelection, setRowSelection] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)

  const useFormHook = useForm<FieldValues>({
    defaultValues: {
      startDate: filterParams?.fromDate || filterParams?.startDate || null,
      endDate: filterParams?.toDate || filterParams?.endDate || null,
      pagination: filterParams?.pagination || { pageIndex: 0, pageSize: 10 },
      sort: filterParams?.sort || null,
      fields: filterParams?.fields || [],
    },
  })

  const watchedStartDate = useFormHook.watch('startDate') as string | null
  const watchedEndDate = useFormHook.watch('endDate') as string | null

  const mergedFilterParams = useMemo(() => {
    const p: Record<string, any> = { ...(filterParams || {}) }
    if (watchedStartDate) {
      p.fromDate = moment(watchedStartDate).format('YYYY-MM-DD')
    } else {
      delete p.fromDate
    }
    if (watchedEndDate) {
      p.toDate = moment(watchedEndDate).format('YYYY-MM-DD')
    } else {
      delete p.toDate
    }
    return p
  }, [filterParams, watchedStartDate, watchedEndDate])

  const requestParams = useMemo(
    () => buildBufferLogsRequestParams(mergedFilterParams || {}),
    [mergedFilterParams]
  )

  const { data, isLoading, isFetching, error, refetch } = useBufferLogs(requestParams, {
    keepPreviousData: true,
  })
  const responseData = data?.data

  const columns: ColumnDef<BufferLogRecord>[] = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        enableSorting: false,
        cell: ({ row }) => (
          <div
            className="max-w-[180px] truncate font-mono text-xs text-gray-900 dark:text-white"
            title={String(row.original?.id ?? '')}
          >
            {row.original?.id ?? '-'}
          </div>
        ),
      },
      {
        header: 'Device ID',
        accessorKey: 'device_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.device_id ?? '-'}</div>
        ),
      },
      {
        header: 'Device Type',
        accessorKey: 'device_type',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.device_type ?? '-'}</div>
        ),
      },
      {
        header: 'Episode ID',
        accessorKey: 'episode_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.episode_id ?? '-'}</div>
        ),
      },
      {
        header: 'User ID',
        accessorKey: 'user_id',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="text-gray-900 dark:text-white">{row.original?.user_id ?? '-'}</div>
        ),
      },
      {
        header: 'Buffer intervals',
        id: 'buffer',
        enableSorting: false,
        cell: ({ row }) => <BufferIntervalsCell intervals={row.original?.buffer} />,
      },
      {
        header: 'Created At',
        accessorKey: 'createdAt',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-gray-900 dark:text-white">
            {formatDateTime(row.original?.createdAt)}
          </div>
        ),
      },
      {
        header: 'Updated At',
        accessorKey: 'updatedAt',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="whitespace-nowrap text-gray-900 dark:text-white">
            {formatDateTime(row.original?.updatedAt)}
          </div>
        ),
      },
    ],
    []
  )

  const filterOptions: FilterOptions[] = useMemo(
    () => [
      {
        key: PLATFORM_FIELD_ID,
        label: 'Device Type',
        options: [{ display: 'All', value: '' }, ...PLATFORM_OPTIONS.map((value) => ({ display: value, value }))],
      },
    ],
    []
  )

  const dateRangeControl = (
    <div className="flex items-center gap-3 justify-end mb-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-300">From</span>
        <input
          type="date"
          className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
          value={watchedStartDate ? moment(watchedStartDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => useFormHook.setValue('startDate', e.target.value || null)}
        />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 dark:text-gray-300">To</span>
        <input
          type="date"
          className="border px-2 py-1 rounded bg-white dark:bg-slate-700 text-sm"
          value={watchedEndDate ? moment(watchedEndDate).format('YYYY-MM-DD') : ''}
          onChange={(e) => useFormHook.setValue('endDate', e.target.value || null)}
        />
      </div>
    </div>
  )

  useEffect(() => {
    const subscriber = useFormHook.watch((value, { name }) => {
      if (name) {
        const updatedParams = getUpdatedSearchParams(filterParams, value, name)
        if (Object.keys(updatedParams)?.length) {
          const sp = encodeURIComponent(JSON.stringify(updatedParams))
          setSearchParams({ filter: sp })
        } else {
          setSearchParams({})
        }
      }
    })

    return () => subscriber.unsubscribe()
  }, [filterParams, setSearchParams, useFormHook])

  const handleRefresh = () => {
    setIsRefreshing(true)
    refetch().finally(() => setIsRefreshing(false))
  }

  if (isLoading) return <PagePreloader />

  if (error) {
    return (
      <SmoothTransition>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error: {error instanceof Error ? error.message : 'An error occurred'}
          </div>
        </div>
      </SmoothTransition>
    )
  }

  return (
    <SmoothTransition>
      <div className="w-full px-6 pt-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
        <CustomTable
          columns={columns}
          data={responseData?.results || []}
          pages={responseData?.pages || 0}
          rowSelectionState={[rowSelection, setRowSelection]}
          isLoading={isFetching || isRefreshing}
          isErrored={!!error}
          formHook={useFormHook}
          title="Buffer Logs"
          onRefresh={handleRefresh}
          totalRecords={responseData?.total}
          titleLabel={`Total ${responseData?.total || 0} records`}
          filterOptions={filterOptions}
          searchVisible={false}
          extraFilterElement={dateRangeControl}
        />
      </div>
    </SmoothTransition>
  )
}

export default BufferLogs
